import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, useLocation } from '@tanstack/react-router'
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  X,
  Save,
  ArrowUp,
  ArrowDown,
  Image as ImageIcon,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { ImageDropzone } from '../components/ImageDropzone'
import { apiGet } from '../lib/api'

interface SliderRow {
  id: string
  title: string
  subtitle: string
  image: string
  imageMobile: string
  ctaText: string
  ctaUrl: string
  sortOrder: number
  isActive: boolean
}

interface SlidersData {
  total: number
  data: SliderRow[]
}

interface SliderForm {
  title: string
  subtitle: string
  image: string
  image_mobile: string
  cta_text: string
  cta_url: string
  sort_order: number
  is_active: boolean
}

const empty: SliderForm = {
  title: '',
  subtitle: '',
  image: '',
  image_mobile: '',
  cta_text: '',
  cta_url: '',
  sort_order: 0,
  is_active: true,
}

export function Slider() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams({ strict: false }) as { id?: string }
  const [editingId, setEditingId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<SliderForm>(empty)
  const [err, setErr] = useState<string | null>(null)

  const { data, isLoading, isFetching } = useQuery<SlidersData>({
    queryKey: ['admin-sliders'],
    queryFn: () => apiGet('/api/admin/sliders'),
  })

  // Sync state to URL: /slider/nuevo -> create, /slider/:id -> edit, /slider -> list
  useEffect(() => {
    const path = location.pathname
    if (path === '/slider/nuevo') {
      setForm(empty)
      setCreating(true)
      setEditingId(null)
    } else if (params.id) {
      setCreating(false)
      setEditingId(params.id)
    } else {
      setCreating(false)
      setEditingId(null)
    }
  }, [location.pathname, params.id])

  useEffect(() => {
    if (editingId && data) {
      const s = data.data.find((x) => x.id === editingId)
      if (s) {
        setForm({
          title: s.title,
          subtitle: s.subtitle,
          image: s.image,
          image_mobile: s.imageMobile ?? '',
          cta_text: s.ctaText,
          cta_url: s.ctaUrl,
          sort_order: s.sortOrder,
          is_active: s.isActive,
        })
      }
    }
  }, [editingId, data])

  const save = useMutation({
    mutationFn: async () => {
      const isEdit = !!editingId
      const url = isEdit ? `/api/admin/sliders/${editingId}` : `/api/admin/sliders`
      const method = isEdit ? 'PUT' : 'POST'
      const body = {
        title: form.title,
        subtitle: form.subtitle,
        image: form.image,
        imageMobile: form.image_mobile,
        cta_text: form.cta_text,
        cta_url: form.cta_url,
        sort_order: Number(form.sort_order) || 0,
        is_active: form.is_active,
      }
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      return j
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-sliders'] })
      closeForm()
    },
    onError: (e: Error) => setErr(e.message),
  })

  const del = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/admin/sliders/${id}`, { method: 'DELETE' })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      return j
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-sliders'] }),
  })

  const reorder = useMutation({
    mutationFn: async ({ id, sort_order }: { id: string; sort_order: number }) => {
      const r = await fetch(`/api/admin/sliders/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sort_order }),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      return j
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-sliders'] }),
  })

  function openCreate() {
    setErr(null)
    navigate({ to: '/slider/nuevo' })
  }

  function openEdit(id: string) {
    setErr(null)
    navigate({ to: '/slider/$id', params: { id } })
  }

  function closeForm() {
    setErr(null)
    navigate({ to: '/slider' })
  }

  function onDelete(s: SliderRow) {
    if (!confirm(`¿Eliminar el slide "${s.title || s.id}"?`)) return
    del.mutate(s.id)
  }

  function move(s: SliderRow, dir: 'up' | 'down') {
    const next = dir === 'up' ? s.sortOrder - 1 : s.sortOrder + 1
    reorder.mutate({ id: s.id, sort_order: next })
  }

  function set<K extends keyof SliderForm>(k: K, v: SliderForm[K]) {
    setForm((f) => ({ ...f, [k]: v }))
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    save.mutate()
  }

  const showForm = creating || !!editingId

  return (
    <>
      <Topbar
        title="Slider de home"
        subtitle={data ? `${data.total} slides` : 'Cargando…'}
        actions={
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="size-3.5" /> Nuevo slide
          </button>
        }
      />

      <div className="p-6">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
              {isLoading && (
                <div className="grid place-items-center py-20 text-ink-500">
                  <Loader2 className="size-8 animate-spin" />
                </div>
              )}

              {data && data.data.length === 0 && (
                <div className="grid place-items-center py-16 text-center">
                  <ImageIcon className="size-10 text-ink-300" />
                  <p className="mt-3 text-sm font-medium text-ink-700">Sin slides aún</p>
                </div>
              )}

              {data && data.data.length > 0 && (
                <ul className="divide-y divide-ink-100">
                  {data.data.map((s) => (
                    <li key={s.id} className="flex items-center gap-4 p-4">
                      <div className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-lg bg-ink-100">
                        {s.image ? (
                          <img
                            src={s.image}
                            alt=""
                            className="size-16 object-cover"
                            onError={(e) => {
                              ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                            }}
                          />
                        ) : (
                          <ImageIcon className="size-5 text-ink-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-ink-900">
                            {s.title || <span className="text-ink-400">(sin título)</span>}
                          </h3>
                          <Badge variant={s.isActive ? 'success' : 'default'}>
                            {s.isActive ? 'Activo' : 'Inactivo'}
                          </Badge>
                          <span className="rounded-full bg-ink-100 px-2 py-0.5 font-mono text-[10px] text-ink-700">
                            #{s.sortOrder}
                          </span>
                        </div>
                        <p className="line-clamp-1 text-xs text-ink-500">{s.subtitle}</p>
                        {s.ctaText && (
                          <p className="mt-1 text-xs text-ink-500">
                            CTA: <span className="font-medium text-ink-700">{s.ctaText}</span> →{' '}
                            <span className="font-mono">{s.ctaUrl}</span>
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => move(s, 'up')}
                          disabled={reorder.isPending}
                          className="rounded-md p-1 text-ink-500 hover:bg-ink-100 disabled:opacity-50"
                          title="Subir"
                        >
                          <ArrowUp className="size-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(s, 'down')}
                          disabled={reorder.isPending}
                          className="rounded-md p-1 text-ink-500 hover:bg-ink-100 disabled:opacity-50"
                          title="Bajar"
                        >
                          <ArrowDown className="size-3.5" />
                        </button>
                      </div>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(s.id)}
                          className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                          title="Editar"
                        >
                          <Edit className="size-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(s)}
                          disabled={del.isPending}
                          className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700 disabled:opacity-50"
                          title="Eliminar"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {isFetching && (
                <div className="border-t border-ink-100 bg-ink-50 px-4 py-2 text-xs text-ink-500">
                  Actualizando…
                </div>
              )}
            </div>
          </div>

          {/* Form lateral */}
          <aside className="lg:sticky lg:top-28 lg:self-start">
            {showForm ? (
              <form
                onSubmit={onSubmit}
                className="space-y-4 rounded-2xl border border-ink-200 bg-white p-5"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-ink-900">
                    {creating ? 'Nuevo slide' : 'Editar slide'}
                  </h3>
                  <button
                    type="button"
                    onClick={closeForm}
                    className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                  >
                    <X className="size-4" />
                  </button>
                </div>

                {err && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                    {err}
                  </div>
                )}

                <Field label="Título">
                  <input
                    value={form.title}
                    onChange={(e) => set('title', e.target.value)}
                    maxLength={180}
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>
                <Field label="Subtítulo">
                  <textarea
                    rows={2}
                    value={form.subtitle}
                    onChange={(e) => set('subtitle', e.target.value)}
                    maxLength={300}
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>
                <Field label="Imagen (URL)">
                  <input
                    value={form.image}
                    onChange={(e) => set('image', e.target.value)}
                    placeholder="/img/sliders/…"
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                  />
                </Field>

                <ImageDropzone
                  label="Imagen para móvil (opcional)"
                  value={form.image_mobile}
                  onChange={(url) => set('image_mobile', url)}
                  hint="opcional"
                />
                <p className="text-[10px] leading-relaxed text-ink-500">
                  Desktop: horizontal (ej. 1920×600). Móvil: vertical/cuadrada (ej. 800×1000). Si no
                  subes móvil, se usa la de escritorio.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Texto CTA">
                    <input
                      value={form.cta_text}
                      onChange={(e) => set('cta_text', e.target.value)}
                      maxLength={80}
                      className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                    />
                  </Field>
                  <Field label="Orden">
                    <input
                      type="number"
                      value={form.sort_order}
                      onChange={(e) => set('sort_order', Number(e.target.value))}
                      className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                    />
                  </Field>
                </div>
                <Field label="URL CTA">
                  <input
                    value={form.cta_url}
                    onChange={(e) => set('cta_url', e.target.value)}
                    placeholder="/catalogo"
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                  />
                </Field>

                <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-ink-200 bg-ink-50 px-3 py-2">
                  <span className="text-sm font-semibold text-ink-700">Activo</span>
                  <span className="relative inline-block h-6 w-11">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => set('is_active', e.target.checked)}
                      className="peer sr-only"
                    />
                    <span className="absolute inset-0 rounded-full bg-ink-200 transition-colors peer-checked:bg-brand-600" />
                    <span className="absolute left-0.5 top-0.5 size-5 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
                  </span>
                </label>

                {form.image && (
                  <div className="overflow-hidden rounded-lg border border-ink-200">
                    <img
                      src={form.image}
                      alt=""
                      className="w-full"
                      onError={(e) => {
                        ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={save.isPending}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {save.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                  Guardar
                </button>
              </form>
            ) : (
              <div className="rounded-2xl border border-dashed border-ink-300 bg-white p-8 text-center text-sm text-ink-500">
                Selecciona un slide para editar o crea uno nuevo.
              </div>
            )}
          </aside>
        </div>
      </div>
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-ink-700">{label}</label>
      {children}
    </div>
  )
}

export default Slider
